import {useEventBus} from '@vueuse/core'
import Echo from 'laravel-echo'
import {defineStore} from 'pinia'
import Pusher from 'pusher-js'
import {useAuthStore} from './auth'

declare global {
    interface Window {
        Echo: Echo<'reverb'>
        Pusher: typeof Pusher
    }
}

export const useWebsocketStore = defineStore('websockets', () => {

    const channel = ref(null)
    const members = ref({})

    async function init(authUserId: number, mode = 'default') {
        const config = useRuntimeConfig()
        window.Pusher = Pusher
        window.Echo = new Echo({
            broadcaster: 'reverb',
            key: config.public.websockets.key,
            host: window.location.hostname,
            httpHost: config.public.websockets.host,
            wsHost: config.public.websockets.host,
            cluster: config.public.websockets.cluster,
            forceTLS: config.public.websockets.forceTLS,
            disableStats: config.public.websockets.disableStats,
            enabledTransports: config.public.websockets.forceWSS ? ['wss'] : ['wss', 'ws'],
            authorizer: (channel: any) => {
                return {
                    authorize: async (socketId: any, callback: any) => {
                        const response = await $lara.post("/app/spa/broadcasting/auth", {
                            body: {
                                socket_id: socketId,
                                channel_name: channel.name
                            }
                        })
                        if (response.error) return callback(true)
                        return callback(false, response)
                    }
                }
            }
        })

        if (mode === 'default') {
            joinUserPrivate()
        }

        window.Echo.private('organisationEvaluations.' + authUserId)
            .listen('.organisation.evaluation.exported', async (response: {
                filePath: string,
                downloadFileName: string
            }) => {
                if (response.filePath) {
                    const {$apiRoute} = useNuxtApp()

                    const download = await $lara.get($apiRoute('spa.organisations.evaluations.show'), {
                        rawResponse: true,
                        query: {
                            filePath: response.filePath
                        }
                    })

                    if (download?.error) {
                        return
                    }

                    useFileSaver().save(download._data, response.downloadFileName)
                }
            })

        window.Echo.private('personPlayingAuthorization.' + authUserId)
            .listen('.person.playingAuthorization.exported', async (response: {
                filePath: string,
                downloadFileName: string
            }) => {
                if (response.filePath) {
                    const {$apiRoute} = useNuxtApp()

                    const download = await $lara.get($apiRoute('spa.organisations.persons.playingAuthorizations.show'), {
                        rawResponse: true,
                        query: {
                            filePath: response.filePath
                        }
                    })

                    if (download?.error) {
                        return
                    }

                    useFileSaver().save(download._data, response.downloadFileName)
                }
            })

        window.Echo.connector.pusher.connection.bind('state_change', function (states: any) {
            if (states.current === 'disconnected') {
                window.Echo.connector.pusher.connect()
            }
        })
    }


    function joinUserPrivate() {
        window.Echo.private('App.Models.User.' + useAuthStore()?.userData?.id)
            .notification((notification: any) => {
                useEventBus('notifications').emit(notification)
            })
            .error((error: Error) => {
                useNotification().error(useNuxtApp().$i18n.t('error'), error.message)
            })
    }

    return {
        init,
        channel,
        members
    }
})